import { localeHeaders } from '../hooks/useLanguage';
import { RANGLISTE_LIMIT, RANGLISTE_STATISTIK_PFAD, architektenPfad, ranglistenPfad } from '../utils/ranglisteAdressen';
import { buildRegistrationPayload } from '../lib/attribution';
import { istVoruebergehenderFehler } from '../utils/sitzungsPruefung';
import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { components } from '../types/api.generated';
import type { Einklappbar } from '../hooks/useEingeklappt';
import type { BrevoStand } from '../shared/BrevoSchluesselKarte';

type VertragsErklaerungIn = components['schemas']['ErklaerungIn'];
export type EmailBestaetigt = components['schemas']['EmailBestaetigtResponse'];
export type CheckoutStatus = components['schemas']['CheckoutStatusResponse'];
export type FaktorAktiviert = components['schemas']['FaktorAktiviertResponse'];
export type Wiederherstellungscodes = components['schemas']['RecoveryCodesResponse'];
export type EinladungsMailVorschauIn = components['schemas']['InvitationMailVorschau'];
export type EinladungsMailVorschau = components['schemas']['InvitationMailVorschauAntwort'];
export type ZweiFaktorHinweis = components['schemas']['HinweisResponse'];
export type ZweiFaktorHinweisAntwort = components['schemas']['HinweisAntwort']['aktion'];
type VertragsBeleg = components['schemas']['BelegOut'];
type VertragsRuecknahme = components['schemas']['RuecknahmeOut'];
export type VertragsErklaerungen = components['schemas']['ErklaerungenOut'];
export type StartHinweisArt = components['schemas']['NutzerStartHinweis']['hinweis'];
export type BudgetStand = components['schemas']['BudgetStand'];
export type VertragsErklaerungAdmin = components['schemas']['ErklaerungAdminOut'];
type WartelisteEintragIn = components['schemas']['EintragIn'];
type WartelisteBestaetigt = components['schemas']['BestaetigenOut'];
export type Warteliste = components['schemas']['WartelisteOut'];
export type WartelisteZeile = components['schemas']['WartelisteZeile'];

/**
 * ID eines Datensatzes.
 *
 * Bewusst `number | string`: in der Datenbank sind das Ganzzahlen, aber die
 * Oberflaeche reicht sie oft direkt aus einem Router-Parameter durch — und
 * `useParams()` liefert grundsaetzlich Strings. Beide Formen landen hier im
 * selben Template-Literal und ergeben dieselbe URL. Ein Typ, der nur `number`
 * zuliesse, wuerde an jeder Aufrufstelle eine Umwandlung erzwingen, die es
 * heute nicht gibt — und damit Laufzeitverhalten aendern.
 */
type Id = number | string;

/**
 * Rumpf eines Requests, dessen Form noch nicht abgebildet ist.
 *
 * `unknown` statt `any` (Regel #19): die Werte werden hier nur durchgereicht,
 * nie gelesen, also entsteht keine Narrowing-Last. Und anders als `any`
 * verhindert es, dass jemand spaeter versehentlich ein Feld anfasst, das es
 * gar nicht gibt. Wer die Form kennt, ersetzt `RequestBody` am jeweiligen
 * Endpunkt durch einen echten Typ — das ist der vorgesehene Weg, nicht die
 * Ausnahme.
 */
type RequestBody = unknown;

/** Query-Parameter, die als `?a=1&b=2` an die URL gehaengt werden. */
type QueryParams = Record<string, unknown>;

/**
 * Request-Konfiguration mit dem Wiederholungs-Merker des 401-Interceptors.
 *
 * `_retry` ist ein vom Frontend angehaengtes Feld, das axios nicht kennt.
 * Ohne diese Erweiterung waere der einzige Ausweg ein `any` — genau der
 * Griff, den Regel #19 verbietet.
 */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Ein Request, der waehrend eines laufenden Token-Refresh wartet.
 *
 * `resolve` nimmt einen Wert entgegen, obwohl der Interceptor keinen
 * uebergibt: die Funktion stammt aus `new Promise(...)` und traegt deren
 * Signatur. Ein parameterloses `() => void` waere hier zu eng.
 */
interface QueuedRequest {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

/**
 * Eingabe des Registrierungsformulars.
 *
 * Jedes Feld gibt es in beiden Schreibweisen, weil zwei Aufrufer mit
 * unterschiedlichen Konventionen darauf zeigen — die Registrierungsseite
 * (camelCase) und die Checkout-Seite (snake_case). Die Zuordnung unten liest
 * deshalb erst die eine, dann die andere.
 *
 * Alle Zustimmungen sind OPTIONAL typisiert und werden unten mit `?? false`
 * abgesichert. Das ist Absicht und darf nicht "aufgeraeumt" werden: ein
 * fehlendes Haekchen muss `false` ergeben, niemals `true`. Genau dieser Fehler
 * ist hier schon einmal passiert — `accept_terms` der Checkout-Seite wurde
 * verworfen und stattdessen fest `true` gesendet.
 */
export interface RegisterInput {
  email: string;
  password: string;
  acceptTerms?: boolean;
  accept_terms?: boolean;
  acceptPrivacy?: boolean;
  accept_privacy?: boolean;
  acceptDisclaimer?: boolean;
  accept_disclaimer?: boolean;
  marketingConsent?: boolean;
  marketing_consent?: boolean;
  language?: string | null;
  country?: string | null;
  market?: string | null;
  requestedPlan?: string | null;
  requested_plan?: string | null;
  requestedBilling?: string | null;
  requested_billing?: string | null;
  requested_currency?: string | null;
  acquisition?: Record<string, unknown> | null;
  partner_code?: string | null;
  team_invitation?: string | null;
}

/** Anmeldung. Das Feld heisst `username`, traegt aber die E-Mail (OAuth2-Form). */
export interface LoginInput {
  username: string;
  password: string;
}

/** Einladung annehmen — die Felder gehen unveraendert ans Backend. */
export interface AcceptInvitationInput {
  username?: string | null;
  password: string;
  acceptTerms?: boolean;
  acceptPrivacy?: boolean;
  acceptDisclaimer?: boolean;
  marketingConsent?: boolean;
}

// Compose/.env nutzen den *_API_BASE-Namen; früher las der Code nur *_API_URL
// → die Variable kam nie an (Default localhost:8000). Seither werden beide
// Namen akzeptiert; leer = relativ (same-origin, Prod via nginx).
// Seit der Vite-Umstellung hiessen sie VITE_* statt REACT_APP_*, seit der
// Next.js-Umstellung (02.10.2026) NEXT_PUBLIC_* — nur diese setzt Next in den
// Browser-Code ein, und zwar zur Build-Zeit: eine Änderung wirkt erst nach
// einem neuen Image.
const API_URL = process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE || '';

const api = axios.create({
  baseURL: API_URL,
  // Phase 4: Cookies werden ab sofort mitgesendet (HttpOnly access/refresh,
  // JS-lesbares CSRF). Bestehender Authorization-Header bleibt parallel
  // aktiv solange localStorage-Token noch nicht aufgeräumt ist (Phase 4c).
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Phase 4c Block D: localStorage-Token-Helper komplett entfernt.
// Auth läuft ausschliesslich über HttpOnly-Cookies (pb_access_token,
// pb_refresh_token), CSRF über das nicht-HttpOnly pb_csrf-Cookie als
// Double-Submit-Token im X-CSRF-Token-Header.

// CSRF-Token aus dem `pb_csrf`-Cookie lesen (vom Backend nach Login gesetzt,
// nicht HttpOnly damit wir das hier dürfen).
const getCsrfToken = () => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)pb_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const CSRF_PROTECTED_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Request Interceptor: nur noch CSRF-Header (HttpOnly-Cookies werden vom
// Browser automatisch mitgesendet — wir können sie sowieso nicht lesen).
api.interceptors.request.use(
  (config) => {
    // Die Adresse reist mit: Die Anfrage geht an `/api/...` und traegt kein
    // Sprach-Praefix, das Backend kann die Browser-Adresse also nicht sehen. Ohne
    // diesen Kopf antwortet es aus dem Cookie — auf `/fr/francais/...` kam so eine
    // deutsche Meldung unter einer franzoesischen Seite (121apps-foundation#140).
    // `search` gehoert dazu: Auf einer flachen Seite aus einem Mail-Link steht
    // die Sprache der Mail als `?lang=` in der Adresse (`languageFromQuery`).
    Object.assign(
      (config.headers = config.headers || {}),
      localeHeaders(window.location.pathname, window.location.search),
    );

    // Definiere wirklich public Endpoints (CSRF nicht nötig)
    const publicAuthEndpoints = [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/refresh',
      '/api/auth/forgot-password',
      '/api/auth/reset-password',
      '/api/auth/verify-email',
      '/api/auth/oauth/complete'  // D2C Social-Signup-Abschluss (Pre-Auth, kein CSRF-Cookie)
    ];

    // Chart-Endpoints sind öffentlich zugänglich (für Arena)
    const isChartEndpoint = config.url?.includes('/chart-data');

    // Hard-Split: /api/news/ lebt in paperball.news (eigene Domain).
    const isPublicEndpoint = config.url?.includes('/api/public/') ||
                             isChartEndpoint ||
                             publicAuthEndpoints.some(ep => config.url?.includes(ep));

    // CSRF-Header bei state-changing Requests (außer Login/Public)
    if (!isPublicEndpoint && CSRF_PROTECTED_METHODS.has((config.method || '').toLowerCase())) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Token-Refresh bei 401
let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

// Phase 4c Block D: kein Token im Resolve mehr — die Cookies sind bereits gesetzt
// und werden vom Browser automatisch an die Retry-Requests gesendet.
const processQueue = (error: unknown) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Bei `responseType: 'blob'` ist auch der FEHLERkörper ein Blob — das Backend
 * hat JSON geschickt, axios hat es als Binärdaten angenommen. Wer den Fehler
 * danach liest (`extractErrorDetail`, `fehlerText`), sah darin nichts, und
 * jeder gescheiterte Download hieß „Ein Fehler ist aufgetreten“, egal was das
 * Backend sagte: Vorlagen, Kursexport, CSV-Exporte der Verwaltung, Chart- und
 * PDF-Export. Die Merkliste hatte dafür eine eigene Behandlung, die `detail`
 * roh zurückgab — am Maschinencode-Filter vorbei (Regel #23).
 *
 * Deshalb wird der Körper hier EINMAL ausgepackt, bevor irgendein Aufrufer ihn
 * sieht; danach ist ein Datei-Fehler ein Fehler wie jeder andere. Registriert
 * vor dem Refresh-Interceptor, damit der Wiederholungsweg denselben Körper
 * sieht. Ein Körper, der kein JSON ist (HTML eines Proxys), bleibt wie er ist
 * und geht laut ins Protokoll — `fehlerText` fällt dann auf den Status zurück.
 */
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const antwort = error.response;
    if (antwort && antwort.data instanceof Blob) {
      const text = await antwort.data.text();
      try {
        antwort.data = JSON.parse(text);
      } catch (lesefehler) {
        console.error('Fehlerkörper einer Datei-Anfrage ist kein JSON:', antwort.status, text.slice(0, 200), lesefehler);
      }
    }
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig;
    
    // Definiere wirklich public Endpoints (keine Login-Weiterleitung bei 401)
    const publicAuthEndpoints = [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/refresh',
      '/api/auth/forgot-password',
      '/api/auth/reset-password',
      '/api/auth/verify-email',
      '/api/auth/oauth/complete',  // D2C Social-Signup-Abschluss (Pre-Auth, kein CSRF-Cookie)
      '/api/auth/invite/'          // Einladung prüfen/annehmen — Pre-Auth, kein Refresh-Versuch
    ];

    // Chart-Endpoints sind öffentlich zugänglich (für Arena)
    const isChartEndpoint = originalRequest.url?.includes('/chart-data');
    
    // Hard-Split: /api/news/ lebt in paperball.news.
    const isPublicEndpoint = originalRequest.url?.includes('/api/public/') ||
                             isChartEndpoint ||
                             publicAuthEndpoints.some(ep => originalRequest.url?.includes(ep));

    // Wenn 401: einmal Refresh versuchen (das Refresh-Cookie ist HttpOnly,
    // wird vom Browser automatisch an /api/auth/refresh mitgesendet).
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Bei public Endpoints: Fehler durchreichen ohne Redirect
      if (isPublicEndpoint) {
        return Promise.reject(error);
      }

      // `?.` gleicht diese Pruefung an die drei Geschwister 15 Zeilen weiter
      // oben an, die `url` seit jeher als optional behandeln. Das ist die
      // einzige Verhaltensaenderung dieser Umstellung, deshalb ausgeschrieben:
      // waere `url` leer, warf diese Zeile bisher einen TypeError MITTEN im
      // Fehler-Interceptor und ersetzte damit den urspruenglichen 401 durch
      // einen Folgefehler. Jetzt laeuft sie in den Refresh-Zweig. Erreichbar
      // ist der Fall nicht — axios setzt `config.url` bei jedem abgesetzten
      // Request, und ohne Request gibt es keine 401-Antwort.
      if (!originalRequest.url?.includes('/auth/refresh')) {
        if (isRefreshing) {
          // Warte auf laufenden Refresh, dann den Original-Request neu aufrufen
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => api(originalRequest))
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Cookie-only Refresh (Backend liest pb_refresh_token aus Cookie,
          // setzt neue pb_access_token + pb_csrf Cookies in der Response)
          await api.post('/api/auth/refresh');
          processQueue(null);

          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          // Nur ein abgelehnter Refresh beendet die Sitzung. Ist der Server
          // gerade nicht erreichbar (502 beim Deploy, 429, Netz), gilt das
          // Refresh-Cookie weiter — den Cache zu löschen hieße, beim nächsten
          // Laden ohne Nutzer dazustehen, nur weil das Backend kurz stand.
          if (!istVoruebergehenderFehler(refreshError)) {
            localStorage.removeItem('user');
          }
          // KEIN automatischer Redirect - AreaRoute/ProtectedRoute kümmert sich darum
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }
    
    // Kein Refresh möglich - User-Cache löschen (Cookies sind serverseitig
    // ohnehin futsch). AreaRoute/ProtectedRoute kümmert sich um Redirect.
    if (error.response?.status === 401 && !isPublicEndpoint) {
      localStorage.removeItem('user');
    }
    
    return Promise.reject(error);
  }
);

// === Auth ===
// Partnerportal. Eigener Cookie (`pbp_session`), eigene Routen — ein
// Partner ist kein Kunde (FLEET_CODES_SPEC §6.2).
export const partnerAPI = {
  login: (daten: { email: string; password: string; totp_code?: string }) =>
    api.post('/api/partner/login', daten),
  logout: () => api.post('/api/partner/logout'),
  inviteAccept: (daten: { token: string; password: string }) =>
    api.post('/api/partner/invite/accept', daten),
  totpSetup: (daten: { email: string; password: string }) =>
    api.post('/api/partner/totp/setup', daten),
  totpActivate: (email: string, daten: { secret: string; code: string }) =>
    api.post(`/api/partner/totp/activate?email=${encodeURIComponent(email)}`, daten),
  me: () => api.get('/api/partner/me'),
  balance: () => api.get('/api/partner/balance'),
  codes: () => api.get('/api/partner/codes'),
  payouts: () => api.get('/api/partner/payouts'),
  beantragen: (daten: { amount_eur_minor: number }) => api.post('/api/partner/payouts', daten),
  bank: () => api.get('/api/partner/bank'),
  bankEintragen: (daten: Record<string, unknown>) => api.post('/api/partner/bank', daten),
  bankPenny: (daten: { code: string }) => api.post('/api/partner/bank/penny', daten),
  // Öffentlich — der Link kommt aus der Mail an die bisherige Adresse.
  bankConfirm: (token: string) => api.post('/api/partner/bank/confirm', null, { params: { token } }),
};

// Auszahlungen und Bankwechsel freigeben (Verwaltung).
export const adminPayoutsAPI = {
  liste: (params?: { state?: string }) => api.get('/api/admin/payouts', { params }),
  iban: (id: number) => api.get(`/api/admin/payouts/${id}/iban`),
  weiter: (id: number, ziel: string, daten: Record<string, unknown> = {}) =>
    api.post(`/api/admin/payouts/${id}/${ziel}`, daten),
  protokoll: (id: number) => api.get(`/api/admin/payouts/${id}/events`),
  offeneBanken: () => api.get('/api/admin/partner-banks'),
  bankFreigeben: (id: number) => api.post(`/api/admin/partner-banks/${id}/approve`, {}),
  bankAblehnen: (id: number, daten: Record<string, unknown>) =>
    api.post(`/api/admin/partner-banks/${id}/reject`, daten),
};

// Codes und Partner verwalten (Verwaltung, `require_users_access`).
// Kündigen (§312k) und Widerrufen (§356a) — öffentlich, ohne Anmeldung.
// Die Antwort hat für jede Eingabe dieselbe Form (FLEET_VERTRAGSENDE_SPEC).
export const vertragsendeAPI = {
  abgeben: (daten: VertragsErklaerungIn) =>
    api.post<VertragsBeleg>('/api/contracts/notices', daten),
  zuruecknehmen: (token: string) =>
    api.post<VertragsRuecknahme>('/api/contracts/notices/revoke', { token }),
};

/**
 * Closed Beta (14.10.2026): Warteliste der Zwischenseite `/beta` und die Ziele
 * der Links aus der Bestätigungsmail. Öffentlich (`/api/public/…`, ohne CSRF).
 */
export const wartelisteAPI = {
  eintragen: (daten: WartelisteEintragIn) => api.post<void>('/api/public/waitlist', daten),
  bestaetigen: (token: string) =>
    api.post<WartelisteBestaetigt>(`/api/public/waitlist/confirm/${encodeURIComponent(token)}`),
  austragen: (id: string, signatur: string) =>
    api.post<void>(`/api/public/waitlist/unsubscribe/${encodeURIComponent(id)}/${encodeURIComponent(signatur)}`),
};

export const adminWartelisteAPI = {
  liste: () => api.get<Warteliste>('/api/admin/waitlist'),
};

export const adminVertragsendeAPI = {
  liste: (params: { open: boolean; ref?: string }) =>
    api.get<VertragsErklaerungen>('/api/admin/contract-notices', { params }),
  erledigt: (id: number) => api.post<VertragsErklaerungAdmin>(`/api/admin/contract-notices/${id}/resolve`),
};

export const adminCodesAPI = {
  codes: (params?: { partner_id?: number }) => api.get('/api/admin/codes', { params }),
  codeAnlegen: (daten: Record<string, unknown>) => api.post('/api/admin/codes', daten),
  codeAendern: (id: number, daten: Record<string, unknown>) => api.patch(`/api/admin/codes/${id}`, daten),
  codeLoeschen: (id: number) => api.delete(`/api/admin/codes/${id}`),
  einloesungen: (id: number) => api.get(`/api/admin/codes/${id}/redemptions`),
  partner: () => api.get('/api/admin/partners'),
  partnerAnlegen: (daten: Record<string, unknown>) => api.post('/api/admin/partners', daten),
  partnerAendern: (id: number, daten: Record<string, unknown>) => api.patch(`/api/admin/partners/${id}`, daten),
  // Legt den Portal-Zugang an und schickt den Einladungslink. Ohne diesen
  // Aufruf kommt ein Partner nie ins Portal — `lade_ein()` gab es, einen
  // Aufrufer nicht.
  partnerEinladen: (id: number, daten: { email?: string }) =>
    api.post(`/api/admin/partners/${id}/invite`, daten),
};

// Gutschein-/Partnercodes. `check` ist oeffentlich (das Feld steht auf der
// Registrierung, dort gibt es noch kein Konto); `redeem` und `mine` nicht.
export const codesAPI = {
  check: (daten: { code: string; plan?: string | null; interval?: string | null; currency?: string | null }) =>
    api.post('/api/codes/check', daten),
  redeem: (daten: { code: string; plan?: string | null; interval?: string | null }) =>
    api.post('/api/codes/redeem', daten),
  mine: () => api.get('/api/codes/mine'),
};

export const authAPI = {
  // Registrierung + UTM-Attribution. Seit 30.09.2026 nur noch E-Mail und
  // Passwort: Name, Mobilnummer, Benutzername und Firma fragt die Seite nicht
  // mehr ab (das Backend nimmt sie weiter optional an).
  register: (data: RegisterInput) => api.post('/api/auth/register', {
    email: data.email,
    password: data.password,
    // Beide Schreibweisen lesen (der Aufrufer ist heute die RegisterCheckout-
    // Page mit snake_case) — und NIE auf `true` defaulten: ein fehlendes
    // Häkchen darf hier nicht zu einer Zustimmung werden (vorher wurde
    // `accept_terms` der Checkout-Seite verworfen und stattdessen immer `true`
    // gesendet, `marketing_consent` immer `false`).
    accept_terms: data.acceptTerms ?? data.accept_terms ?? false,
    accept_privacy: data.acceptPrivacy ?? data.accept_privacy ?? false,
    accept_disclaimer: data.acceptDisclaimer ?? data.accept_disclaimer ?? false,
    marketing_consent: data.marketingConsent ?? data.marketing_consent ?? false,
    // Die Sprache der Oberfläche. Ohne Vorgabe: Bis 16.09.2026 stand hier
    // `?? 'de'`, und das Formular schickte gar keine — jede Fehlermeldung
    // der Registrierung kam auf Deutsch zurück, auf jeder Länderseite, und
    // `users.language` stand bei jedem neuen Konto auf `de`.
    language: data.language ?? null,
    // Wohnsitz und Länderversion — ohne Vorgabe: Das Backend entscheidet
    // über beide, ob hier ein Konto entstehen darf, und ein fehlendes Land
    // ist keine Angabe „DE".
    country: data.country ?? null,
    market: data.market ?? null,
    requested_plan: data.requestedPlan ?? data.requested_plan ?? null,
    requested_billing: data.requestedBilling ?? data.requested_billing ?? null,
    // Die Währung der Preisseite (`?currency=`) — der Client schreibt die
    // Felder einzeln um, was hier fehlt, kommt nie an.
    requested_currency: data.requested_currency ?? null,
    acquisition: data.acquisition || null,
    // Gutschein-/Partnercode. Stand bis 29.09.2026 NICHT in dieser Liste: Die
    // Registrierung sammelte den Code (samt Live-Prüfung), und hier fiel er
    // weg — das Backend (`RegisterRequest.partner_code`) sah ihn nie.
    partner_code: data.partner_code ?? null,
    // Token einer Team-Einladung (`/register?invitation=…`): das neue Konto
    // tritt dem Team direkt bei.
    team_invitation: data.team_invitation ?? null,
  }),

  // Login (OAuth2 Form) — `username`-Feld trägt die E-Mail.
  login: (data: LoginInput) => {
    const formData = new URLSearchParams();
    formData.append('username', data.username);
    formData.append('password', data.password);
    return api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  // 2. Login-Schritt (Auth v2 Challenge): TOTP bzw. SMS.
  loginTotp: (email: string, code: string, preAuthToken: string) => api.post('/api/auth/login/totp', { email, code, pre_auth_token: preAuthToken }),
  loginSms: (email: string, code: string, preAuthToken: string) => api.post('/api/auth/login/sms', { email, code, pre_auth_token: preAuthToken }),
  // Statt App oder SMS: ein Wiederherstellungscode (gilt einmal).
  loginRecovery: (email: string, code: string, preAuthToken: string) => api.post('/api/auth/login/recovery', { email, code, pre_auth_token: preAuthToken }),
  
  // Token Refresh
  refresh: (refreshToken: string) => api.post('/api/auth/refresh', { refresh_token: refreshToken }),
  
  // Logout — Backend liest Refresh-Token aus Cookie; Body bleibt zur
  // Backward-Kompatibilität optional (alte Frontend-Builds könnten noch
  // den Token im Body senden, das Backend akzeptiert beides).
  logout: (refreshToken: string) => api.post('/api/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}),
  logoutAll: (currentRefreshToken: string) => api.post('/api/auth/logout-all', currentRefreshToken ? { current_refresh_token: currentRefreshToken } : {}),
  
  // Profil
  getMe: () => api.get('/api/auth/me'),
  getSettings: () => api.get('/api/auth/me/settings'),
  updateSettings: (data: RequestBody) => api.put('/api/auth/me/settings', data),
  // Kennzahl-Auswahl je Sicht (Fundament-Baustein MetricSlots) — am Nutzer gespeichert.
  getMetricLayouts: () => api.get('/api/auth/me/metric-layout'),
  // Eine Erklärung ein- oder aufklappen, am Konto gemerkt (hooks/useEingeklappt.ts).
  setEinfuehrung: (kennung: Einklappbar, geschlossen: boolean) =>
    api.put(`/api/auth/me/guides/${kennung}`, { geschlossen }),
  updateMetricLayout: (view: string, order: string[]) => api.put('/api/auth/me/metric-layout', { view, order }),
  // Rechtshinweis vor dem Start (components/starthinweis/): erscheint er, und „nicht mehr anzeigen“.
  getStartHinweise: () => api.get<components['schemas']['StartHinweiseAntwort']>('/api/auth/me/start-hinweise'),
  blendeStartHinweisAus: (hinweis: StartHinweisArt) =>
    api.put<components['schemas']['StartHinweiseAntwort']>(`/api/auth/me/start-hinweise/${hinweis}`),
  // Der einblendbare Zeitverlauf je Sicht (components/metrics/useZeitverlauf.ts).
  getZeitverlauf: () => api.get<components['schemas']['ZeitverlaufEinstellungen']>('/api/auth/me/zeitverlauf'),
  updateZeitverlauf: (sicht: 'watchlist' | 'my_portfolios', stand: components['schemas']['ZeitverlaufEinstellung']) =>
    api.put<components['schemas']['ZeitverlaufEinstellungen']>(`/api/auth/me/zeitverlauf/${sicht}`, stand),
  // Konto beenden (E20): neue Adresse wird vorgemerkt und erst mit dem
  // Bestätigungslink übernommen; die Auskunft ist ein Download-Link auf
  // `/api/settings/export` (Cookie-Anmeldung, kein Aufruf hier nötig).
  changeEmail: (newEmail: string, password: string) => api.post('/api/settings/email', { new_email: newEmail, password }),
  deleteAccount: (password: string, confirmation: string) => api.delete('/api/settings/account', { data: { password, confirmation } }),
  // Support-Zugriff (seit 17.09.2026): Einwilligung, dass der Plattform-Support
  // das Konto öffnet — selbst erteilt oder als Antwort auf eine Anfrage.
  getSupportAccess: () => api.get('/api/settings/support-access'),
  grantSupportAccess: (duration: '1h' | '24h' | '7d') => api.post('/api/settings/support-access', { duration }),
  revokeSupportAccess: () => api.delete('/api/settings/support-access'),
  declineSupportRequest: (requestId: Id) => api.post(`/api/settings/support-access/requests/${requestId}/decline`),
  changePassword: (currentPassword: string, newPassword: string) => api.post('/api/auth/me/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
  
  // Features & Limits
  getFeatures: () => api.get('/api/auth/me/features'),
  
  // E-Mail-Verifizierung — Link ODER Code aus derselben Mail. Beide melden
  // bei der Registrierung direkt an (`angemeldet: true`, Sitzung als Cookie).
  verifyEmail: (token: string) => api.post<EmailBestaetigt>('/api/auth/verify-email', { token }),
  verifyEmailCode: (email: string, code: string) => api.post<EmailBestaetigt>('/api/auth/verify-email/code', { email, code }),
  // Anonym (Hard-Gate: unverifizierter User kann sich nicht einloggen).
  // Schickt NEUEN Code und neuen Link; der vorige Code gilt danach nicht mehr.
  resendVerification: (email: string) => api.post('/api/auth/resend-verification', { email }),

  // === 2FA-Verwaltung (Auth v2) ===
  totpSetup: () => api.post('/api/security/totp/setup'),
  // Beim ERSTEN Faktor trägt die Antwort die Wiederherstellungscodes — einmal.
  totpActivate: (secret: string, code: string, password: string) => api.post<FaktorAktiviert>('/api/security/totp/activate', { secret, code, password }),
  totpDisable: (password: string) => api.delete('/api/security/totp', { data: { password } }),
  // Ohne hinterlegte Nummer gehen Nummer und Passwort mit (`has_phone`).
  smsSendCode: (neu?: { phone: string; password: string }) => api.post('/api/security/sms/send-code', neu ?? null),
  smsActivate: (code: string) => api.post<FaktorAktiviert>('/api/security/sms/activate', { code }),
  smsDisable: (password: string) => api.delete('/api/security/sms', { data: { password } }),
  recoveryCodesNeu: (password: string) => api.post<Wiederherstellungscodes>('/api/security/recovery-codes', { password }),
  // Hinweis „Schütze dein Konto mit einem zweiten Faktor" — Zustand am Konto.
  zweiFaktorHinweis: () => api.get<ZweiFaktorHinweis>('/api/security/hinweis'),
  zweiFaktorHinweisBeantworten: (aktion: ZweiFaktorHinweisAntwort) => api.post<ZweiFaktorHinweis>('/api/security/hinweis', { aktion }),

  // Impersonation (Admin-Account-Switching)
  startImpersonation: (userId: Id) => api.post(`/api/admin/users/${userId}/impersonate`),
  stopImpersonation: () => api.post('/api/admin/impersonation/stop'),
  getImpersonationStatus: () => api.get('/api/admin/impersonation/current'),
  
  // Passwort-Reset
  forgotPassword: (email: string) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string, smsCode: string) => api.post('/api/auth/reset-password', {
    token,
    new_password: newPassword,
    sms_code: smsCode || undefined,
  }),
  
  // Sessions
  getSessions: (currentRefreshToken: string) => api.get('/api/auth/sessions', {
    params: { current_refresh_token: currentRefreshToken }
  }),
  deleteSession: (sessionId: Id) => api.delete(`/api/auth/sessions/${sessionId}`),
  
  // Utilities
  getCurrencies: () => api.get('/api/auth/currencies'),

  // === Einladungen (Pre-Auth) ===
  // Über den geteilten Client, NICHT über rohes fetch(): nur so geht bei einem
  // bereits angemeldeten Besucher der X-CSRF-Token-Header mit. Ohne ihn hat das
  // Annehmen mit „CSRF-Token fehlt oder ungültig" (403) abgebrochen.
  checkInvitation: (token: string) => api.get(`/api/auth/invite/${token}`),
  acceptInvitation: (token: string, data: AcceptInvitationInput) =>
    api.post(`/api/auth/invite/${token}/accept`, {
      username: data.username,
      password: data.password,
      accept_terms: data.acceptTerms,
      accept_privacy: data.acceptPrivacy,
      accept_disclaimer: data.acceptDisclaimer,
      marketing_consent: data.marketingConsent,
    }),

  // === D2C Social-Login (eingehende OAuth) ===
  // Konfigurierte Provider — das Frontend zeigt nur diese Buttons.
  getOAuthProviders: () => api.get('/api/auth/oauth/providers'),
  // Vorbelegung des Social-Signup-Abschlusses (E-Mail/Name read-only + Firmenname-Vorschlag).
  getOAuthPending: (token: string) => api.get('/api/auth/oauth/pending', { params: { token } }),
  // Neuen Social-Nutzer nach Firmenname + AGB- und Risikohinweis-Checkbox
  // anlegen → Session-Cookies.
  /**
   * Schliesst eine Registrierung über einen Anbieter ab.
   *
   * `acquisition` MUSS mit — sonst kommt jeder, der sich über Google
   * anmeldet, ohne jede Herkunft an: Das Backend liest `body.acquisition`
   * (`OAuthCompleteRequest`), und `schreibe_akquise` steigt bei `None` sofort
   * aus. Der Nutzer landet dann nicht unter „direkt", sondern unter „nie
   * erhoben" — `acquisition_captured_at` bleibt leer. Ein Partnerlink, der
   * auf „Mit Google anmelden" endet, wäre damit nicht zuordenbar.
   */
  completeSocialSignup: (
    token: string,
    accountName: string,
    country: string,
    acceptedTerms: boolean,
    acceptedDisclaimer: boolean,
    acquisition: ReturnType<typeof buildRegistrationPayload>,
  ) =>
    api.post('/api/auth/oauth/complete', {
      token,
      account_name: accountName,
      country,
      accepted_terms: acceptedTerms,
      accepted_disclaimer: acceptedDisclaimer,
      acquisition,
    }),
};

// === Admin Roles & Features ===
// MCP-API-Keys (User-CRUD)
export const apiKeysAPI = {
  list: () => api.get('/api/user/api-keys'),
  usage: () => api.get('/api/user/api-keys/usage'),  // §7.2 Monats-Nutzung + Kontingent
  create: (data: RequestBody) => api.post('/api/user/api-keys', data),
  revoke: (id: Id) => api.delete(`/api/user/api-keys/${id}`),
  rename: (id: Id, name: string) => api.patch(`/api/user/api-keys/${id}`, { name }),
  // „Löschen über KI-Assistenten und API“ — Schalter des Accounts, nur Inhaber
  // und Admins legen ihn um (Antwort `{erlaubt, darf_aendern, wirksam}`,
  // Baustein `mcp_loeschen.schalter_stand`; `services/mcp_loeschen.py`).
  getLoeschSchalter: () => api.get<components['schemas']['ApiDeleteSchalterResponse']>('/api/user/api-keys/mcp-loeschen'),
  setLoeschSchalter: (erlaubt: boolean) =>
    api.put<components['schemas']['ApiDeleteSchalterResponse']>('/api/user/api-keys/mcp-loeschen', { erlaubt }),
  // Org-Admin (Owner)
  getOrgAllowToggle: (orgId: Id) => api.get(`/api/team/organizations/${orgId}/allow-member-api-keys`),
  setOrgAllowToggle: (orgId: Id, allow: boolean) => api.put(`/api/team/organizations/${orgId}/allow-member-api-keys`, { allow }),
  listOrgKeys: (orgId: Id) => api.get(`/api/team/organizations/${orgId}/api-keys`),
  revokeOrgKey: (orgId: Id, keyId: Id) => api.delete(`/api/team/organizations/${orgId}/api-keys/${keyId}`),
  // Verbundene Apps des Teams (Owner): fremde Verbindungen trennt nur er,
  // die eigene jede Person selbst (`oauthProviderAPI`).
  listOrgGrants: (orgId: Id) =>
    api.get<components['schemas']['OrgGrantOverview'][]>(`/api/team/organizations/${orgId}/oauth-grants`),
  revokeOrgGrant: (orgId: Id, grantId: Id) => api.delete(`/api/team/organizations/${orgId}/oauth-grants/${grantId}`),
};

// OAuth-2.1-Provider (Consent-Screen + „Verbundene Apps") — Spec §4.2, Welle 5.
// Der Token-/Register-Flow läuft ohne Cookie über claude.ai; hier nur die
// eingeloggten Screens: Consent-Kontext/-Entscheidung + Grants-Verwaltung.
export const oauthProviderAPI = {
  authorizeContext: (params: QueryParams) => api.get('/api/oauth/authorize/context', { params }),
  authorizeDecision: (data: RequestBody) => api.post('/api/oauth/authorize/decision', data),
  listGrants: () => api.get('/api/oauth/grants'),
  revokeGrant: (id: Id) => api.delete(`/api/oauth/grants/${id}`),
};

// Outbound-Webhooks (Fleet-MCP/API-Spec §6) — user-scoped Subscriptions +
// Delivery-Log. Signatur X-Paperball-Signature; Secret wird genau 1× angezeigt.
export const webhooksAPI = {
  listEvents: () => api.get('/api/user/webhooks/events'),
  list: () => api.get('/api/user/webhooks'),
  create: (data: RequestBody) => api.post('/api/user/webhooks', data),
  remove: (id: Id) => api.delete(`/api/user/webhooks/${id}`),
  rotateSecret: (id: Id) => api.post(`/api/user/webhooks/${id}/rotate-secret`),
  sendTest: (id: Id) => api.post(`/api/user/webhooks/${id}/test`),
  deliveries: (id: Id) => api.get(`/api/user/webhooks/${id}/deliveries`),
};

// MCP Plattformadmin
export const adminMcpAPI = {
  getConfig: () => api.get('/api/admin/mcp/config'),
  updateConfig: (data: RequestBody) => api.put('/api/admin/mcp/config', data),
  getStats: (days = 7) => api.get('/api/admin/mcp/stats', { params: { days } }),
};

// Public Support-Chat-Status (für Frontend-Auto-Embed)
export const supportChatAPI = {
  getStatus: () => api.get('/api/public/support-chat'),
};

// Stripe-Subscriptions (Self-Service-Abo basic/pro)
export const billingAPI = {
  checkout: (data: RequestBody) => api.post('/api/billing/checkout', data),  // { plan_code, interval }
  change: (data: RequestBody) => api.post('/api/billing/change', data),       // in-place Plan-Wechsel (Proration)
  preview: (data: RequestBody) => api.post('/api/billing/preview', data),     // Proration-Vorschau
  // `account` = das Abo des Accounts, dessen Inhaber man ist (eigener Stripe-Kunde).
  portal: (scope: 'user' | 'account') => api.post('/api/billing/portal', null, { params: { scope } }),
  status: () => api.get('/api/billing/status'),
  catalog: () => api.get('/api/billing/catalog'),
  // Die bei der Registrierung getroffene Tarifwahl — `null`, wenn keine offen
  // ist. Sie wurde seit jeher gespeichert und nie gelesen.
  signupIntent: () => api.get('/api/billing/signup-intent'),
  verwirfSignupIntent: () => api.delete('/api/billing/signup-intent'),
  // Die Anmeldung führt eine Wahl genau EINMAL von selbst zur Kasse —
  // `{ weiterleiten: true }` nur beim ersten Aufruf (`AnmeldeZiel`).
  beanspruchWeiterleitung: () => api.post('/api/billing/signup-intent/weiterleitung'),
  // Öffentlich (kein Auth) — für die /billing/return-Rücksprungseite.
  checkoutStatus: (sessionId: Id) => api.get<CheckoutStatus>('/api/billing/checkout-status', { params: { session_id: sessionId } }),
  // Geschenk mit „Karte bis zum Ende“ (FLEET_CODES_SPEC §4.12): Zahlungsmethode
  // fürs Weiterlaufen hinterlegen bzw. das Weiterlaufen abwählen.
  folgeaboCheckout: (scope: 'user' | 'account') => api.post('/api/billing/folgeabo/checkout', null, { params: { scope } }),
  folgeaboAbwaehlen: (scope: 'user' | 'account') => api.post('/api/billing/folgeabo/abwaehlen', null, { params: { scope } }),
};

export const adminRolesAPI = {
  // Features
  listFeatures: (category: string) => api.get('/api/admin/roles/features', { params: { category } }),
  createFeature: (data: RequestBody) => api.post('/api/admin/roles/features', data),
  updateFeature: (id: Id, data: RequestBody) => api.put(`/api/admin/roles/features/${id}`, data),
  deleteFeature: (id: Id) => api.delete(`/api/admin/roles/features/${id}`),
  updateFeaturesSortOrder: (items: string[]) => api.put('/api/admin/roles/features/sort', { items }),
  
  // Roles
  listRoles: (includeInactive = false) => api.get('/api/admin/roles', { params: { include_inactive: includeInactive } }),
  createRole: (data: RequestBody) => api.post('/api/admin/roles', data),
  updateRole: (id: Id, data: RequestBody) => api.put(`/api/admin/roles/${id}`, data),
  deleteRole: (id: Id) => api.delete(`/api/admin/roles/${id}`),
  updateRolesSortOrder: (items: string[]) => api.put('/api/admin/roles/sort', { items }),
  
  // User-Role Management
  
  // Stats
  getStats: () => api.get('/api/admin/roles/stats'),
};

// === Starter Datasets ===

// === Simulation Tables ===
export const tablesAPI = {
  list: () => api.get('/api/simulation/tables'),
  get: (id: Id) => api.get(`/api/simulation/tables/${id}`),
  create: (data: RequestBody) => api.post('/api/simulation/tables', data),
  delete: (id: Id) => api.delete(`/api/simulation/tables/${id}`),
  rename: (id: Id, name: string) => api.patch(`/api/simulation/tables/${id}/name`, { name }),
  setClientTag: (id: Id, client_tag: string) => api.patch(`/api/simulation/tables/${id}/client-tag`, { client_tag }),
  copy: (id: Id, name: string) => api.post(`/api/simulation/tables/${id}/copy`, { name }),
  export: (id: Id) => api.post(`/api/simulation/tables/${id}/export`, {}, { responseType: 'blob' }),
  getCleanupInfo: (id: Id) => api.get(`/api/simulation/tables/${id}/cleanup-info`),
  validate: (id: Id) => api.get(`/api/simulation/tables/${id}/validate`),
  // Item 2 — Format-Bruch: securities ist jetzt Array von {name_or_isin, invested_amount_eur?}.
  // Strings im Array werden vom Backend backwards-kompatibel als {name_or_isin, invested_amount_eur:null} interpretiert.
  // Data Sync API
  getDataStatus: (id: Id) => api.get(`/api/simulation/tables/${id}/data-status`),
  syncData: (id: Id) => api.post(`/api/simulation/tables/${id}/sync-data`),
  linkToFunds: (id: Id) => api.post(`/api/simulation/tables/${id}/link-to-funds`),
  // Eigene Verteilungen — gelten nur für mich (`user`) oder den Account
  // (`organization`), nie für alle Nutzer (seit 15.09.2026).
  bulkUploadDistributions: (tableId: Id, files: File[], scope: string) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('scope', scope);
    return api.post(`/api/simulation/tables/${tableId}/distributions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteOwnDistribution: (tableId: Id, isin: string, scope: string) =>
    api.delete(`/api/simulation/tables/${tableId}/distributions/${isin}`, { params: { scope } }),
};

// === Merkliste fuer einzelne Wertpapiere ===
// Die Antwort von `folders` traegt `zuordnung` und `wurzel` mit — beides kommt
// aus dem Backend und NICHT aus einer Konstante hier: sonst driftet genau diese
// Konstante von der Wahrheit weg, und der Loeschdialog fragt im falschen Moment
// (Fleet-Spec FLEET_FOLDERS_SPEC, Regel 7).
/** Wie eine Kursdatei gelesen wird — alles Weggelassene erkennt das Backend. */
export interface KursDateiEinstellungen {
  separator: string;
  decimal: string;
  dateFormat: string;
  dateColumn: number;
  priceColumn: number;
}

export interface KursDateiZiel {
  /** 'user' | 'organization' | 'platform' — was die Liste unter `upload_scopes` anbietet. */
  scope: string;
  beiDubletten?: 'ergaenzen' | 'ersetzen';
  /** Bestehendes Wertpapier ergaenzen … */
  itemId?: Id;
  /** … oder ein neues anlegen. */
  isin?: string;
  name?: string;
  currency?: string;
  ertragsverwendung?: string;
  instrumentType?: string;
  wkn?: string;
  settings?: Partial<KursDateiEinstellungen>;
}

/** Ein Datei-Upload der Verwaltung — das Ziel steckt in der ISIN, die Stufe ist `platform`. */
export interface KatalogDateiZiel {
  beiDubletten?: 'ergaenzen' | 'ersetzen';
  /** Nur, wenn das Papier noch keine Währung hat. */
  currency?: string;
  settings?: Partial<KursDateiEinstellungen>;
}

function einstellungenAnhaengen(formData: FormData, settings?: Partial<KursDateiEinstellungen>) {
  if (!settings) return;
  if (settings.separator) formData.append('separator', settings.separator);
  if (settings.decimal) formData.append('decimal', settings.decimal);
  if (settings.dateFormat) formData.append('date_format', settings.dateFormat);
  if (settings.dateColumn !== undefined) formData.append('date_column', String(settings.dateColumn));
  if (settings.priceColumn !== undefined) formData.append('price_column', String(settings.priceColumn));
}

export const watchlistAPI = {
  // Gesamtertrag mehrerer Wertpapiere, am gemeinsamen Start = 100 — der
  // Dialog „Im Chart vergleichen".
  /** „Was ändert sich, wenn ich das hinzufüge?" — ein Lauf im Hintergrund
   * (`services/hinzufuegen_testen.py`); der Stand wird abgefragt. */
  portfolioTestStarten: (daten: components['schemas']['PortfolioTestStart']) =>
    api.post<components['schemas']['PortfolioTestGestartet']>('/api/watchlist/portfolio-test', daten),
  /** Die Nachher-Aufteilung als neues gespeichertes Portfolio (nur aus einem gespeicherten Portfolio). */
  portfolioTestSpeichern: (daten: components['schemas']['PortfolioTestSpeichern']) =>
    api.post<components['schemas']['PortfolioTestGespeichert']>('/api/watchlist/portfolio-test/speichern', daten),
  portfolioTestStand: (laufId?: string | null) =>
    api.get<components['schemas']['PortfolioTestStand']>('/api/watchlist/portfolio-test', {
      params: laufId ? { lauf_id: laufId } : {},
    }),
  vergleich: (isins: string[], window: string, currency = 'EUR', von?: string | null, bis?: string | null) =>
    api.get<components['schemas']['VergleichAntwort']>('/api/watchlist/vergleich', {
      params: { isins: isins.join(','), window, currency, ...(window === 'custom' ? { from: von, to: bis } : {}) },
    }),
  // Rückgang und Erholung eines Wertpapiers in den Krisen des Katalogs —
  // erst beim Aufklappen des Abschnitts „Verhalten in Krisen".
  krisen: (isin: string) =>
    api.get<components['schemas']['KrisenAntwort']>(`/api/watchlist/items/${encodeURIComponent(isin)}/krisen`),
  // Fundamentaldaten einer Aktie (`services/fundamentaldaten`) — `null`, solange nichts geholt ist.
  fundamentals: (isin: string) =>
    api.get<components['schemas']['FundamentalAntwort'] | null>(`/api/watchlist/items/${encodeURIComponent(isin)}/fundamentals`),
  // Korrelation der Monatsrenditen im gemeinsamen Fenster — erst beim
  // Aufklappen des Abschnitts gerufen.
  korrelation: (isins: string[], currency = 'EUR') =>
    api.get<components['schemas']['KorrelationAntwort']>('/api/watchlist/korrelation', {
      params: { isins: isins.join(','), currency },
    }),
  // Kennzahlen je Wertpapier und Zeitraum — der einblendbare Abschnitt
  // „Im Zeitverlauf". Eigene Route, damit die Liste nicht auf die Rechnung wartet.
  zeitverlauf: (zeitraeume: string[], riskFreeRate = 0, currency = 'EUR') =>
    api.get<components['schemas']['ZeitverlaufAntwort']>('/api/watchlist/zeitverlauf', {
      params: { zeitraeume: zeitraeume.join(','), risk_free_rate: riskFreeRate, currency },
    }),
  // `currency` ist ein ISO-Code oder 'original' (die Waehrung des jeweiligen
  // Papiers). Welche Codes erlaubt sind, sagt die Antwort in `currencies` —
  // eine eigene Liste hier waere die zweite Wahrheit und liefe weg.
  // `from`/`to` gehoeren zum freien Fenster `window=custom`; `portfolio`
  // filtert auf die Wertpapiere eines gespeicherten Portfolios (der Weg aus
  // „Meine Portfolios", siehe `shared/wertpapierLink.ts`). Nicht gesetzte
  // Werte werden nicht gesendet — axios laesst `undefined` weg.
  list: (
    window = '5y',
    riskFreeRate = 0,
    currency = 'EUR',
    zeitraum: { from?: string | null; to?: string | null; portfolio?: string | null } = {},
  ) =>
    api.get('/api/watchlist/folders', {
      params: {
        window,
        risk_free_rate: riskFreeRate,
        currency,
        from: zeitraum.from ?? undefined,
        to: zeitraum.to ?? undefined,
        portfolio: zeitraum.portfolio ?? undefined,
      },
    }),

  createFolder: (name: string, shared = false) =>
    api.post('/api/watchlist/folders', { name, shared }),
  updateFolder: (id: Id, data: { name?: string; shared?: boolean; aktualisierung?: 'manuell' | 'bei_aufruf' | 'taeglich' | 'woechentlich' | 'monatlich' }) =>
    api.patch(`/api/watchlist/folders/${id}`, data),
  reorderFolders: (folderIds: Id[]) =>
    api.put('/api/watchlist/folders/order', { folder_ids: folderIds }),
  deleteFolder: (id: Id) => api.delete(`/api/watchlist/folders/${id}`),

  add: (isin: string, folderIds: Id[] = [], note?: string) =>
    api.post('/api/watchlist/items', { isin, folder_ids: folderIds, note }),

  // Mehrere in EINER Transaktion. Antwortet pro ISIN (added / already_present
  // / unknown) statt mit einem pauschalen Erfolg — sonst weiss der Aufrufer
  // nicht, welche von zwanzig es nicht gab.
  addMany: (isins: string[], folderIds: Id[] = []) =>
    api.post('/api/watchlist/items/bulk', { isins, folder_ids: folderIds }),
  // Fortschritt des Hintergrundlaufs, der die ISINs der Liste holt, die es bei
  // uns noch nicht gab (`importing` in der Antwort von `addMany`).
  addManyStatus: () => api.get('/api/watchlist/items/bulk/status'),
  setNote: (id: Id, note: string | null) => api.patch(`/api/watchlist/items/${id}`, { note }),
  // Der Vergleichsmassstab DIESES Wertpapiers (Beta, Alpha, Vergleichslinie).
  // `null` heisst zurueck auf den Standard der Plattform. Nur das eine Feld
  // im Body: PATCH aendert, was gesendet wird, und laesst die Notiz stehen.
  setBenchmark: (id: Id, benchmarkId: number | null) =>
    api.patch(`/api/watchlist/items/${id}`, { benchmark_id: benchmarkId }),
  setFolders: (id: Id, folderIds: Id[]) =>
    api.put(`/api/watchlist/items/${id}/folders`, { folder_ids: folderIds }),

  // Mehrere auf einmal in EINEN Ordner — hinzufuegen, nicht verschieben.
  // Am Ordner haengend, nicht am Wertpapier: die Aktion hat ein Ziel und
  // viele Objekte.
  addManyToFolder: (folderId: Id, itemIds: Id[]) =>
    api.post(`/api/watchlist/folders/${folderId}/items`, { item_ids: itemIds }),
  removeManyFromFolder: (folderId: Id, itemIds: Id[]) =>
    api.delete(`/api/watchlist/folders/${folderId}/items`, { params: { item_ids: itemIds.join(',') } }),

  // Vor dem Entfernen fragen, WAS passieren wuerde. Zwei Dinge kommen zurueck:
  // `action` — wann gefragt wird, entscheidet der Fundament-Baustein und nicht
  // das Frontend — und `datasets`, die Datensaetze, die dieses Wertpapier
  // benutzen. Ist die Liste nicht leer, wird IMMER gefragt: der Baustein kennt
  // nur Ordner, und aus dem Bestand loeschen laesst sich nur, was in keinem
  // Datensatz mehr steht.
  removalPlan: (id: Id, folderId?: Id) =>
    api.get(`/api/watchlist/items/${id}/removal-plan`, { params: folderId ? { folder_id: folderId } : {} }),
  remove: (id: Id, scope: 'folder' | 'all', folderId?: Id) =>
    api.delete(`/api/watchlist/items/${id}`, { params: { scope, ...(folderId ? { folder_id: folderId } : {}) } }),

  toDataset: (itemIds: Id[], tableId?: Id, name?: string) =>
    api.post('/api/watchlist/to-dataset', { item_ids: itemIds, table_id: tableId, name }),

  // `modus` trennt die beiden Haelften: 'kurse' holt nur, 'kennzahlen' rechnet
  // nur (ohne Anbieter-Abruf), 'alles' macht beides.
  refresh: (itemIds?: Id[], riskFreeRate = 0, modus: 'alles' | 'kurse' | 'kennzahlen' = 'alles') =>
    api.post('/api/watchlist/refresh', { item_ids: itemIds }, { params: { risk_free_rate: riskFreeRate, modus } }),
  refreshStatus: () => api.get('/api/watchlist/refresh/status'),
  // Nach jeder automatischen Aktualisierung eine E-Mail — je Person und Ordner.
  setOrdnerMail: (folderId: Id, mail: boolean) => api.put(`/api/watchlist/folders/${folderId}/mail`, { mail }),
  // Der Kursverlauf — dieselbe Reihe, aus der die Kennzahlen kommen (in der
  // gewaehlten Waehrung, eigene Uploads mitgefuehrt). NICHT
  // `funds/{isin}/price-history`: die liest roh und ohne Umrechnung. Die
  // Antwort nennt in `currency` den aufgeloesten Code — bei 'original' steht
  // dort die Waehrung des Papiers, nie das Wort selbst.
  priceHistory: (isin: string, window: string, currency = 'EUR', from?: string | null, to?: string | null) =>
    api.get(`/api/watchlist/items/${isin}/prices`, {
      params: { window, currency, from: from ?? undefined, to: to ?? undefined },
    }),
  // Die Kurse als CSV — dieselbe Reihe wie Kennzahlen und Chart, also mit
  // eigenen Uploads und in der gewaehlten Waehrung, und immer die VOLLE
  // Historie. `responseType: 'blob'`, weil eine Datei kommt; im Fehlerfall
  // steckt die Meldung deshalb AUCH in einem Blob und muss erst gelesen
  // werden — das tut der Fehler-Interceptor oben, fuer jeden Datei-Abruf.
  // Die Kennzahlen aller gemerkten Wertpapiere als Datei (CSV frei, Excel/ODS
  // ab dem Merkmal `excel_export`) — dieselben Zahlen wie die Ansicht.
  // `isins`: nur diese Wertpapiere (der Export je Ordner); ohne Angabe alle.
  exportKennzahlen: (format: string, window: string, riskFreeRate = 0, currency = 'EUR', von?: string | null, bis?: string | null, isins?: string[]) =>
    api.get(`/api/watchlist/kennzahlen.${format}`, {
      params: {
        window, risk_free_rate: riskFreeRate, currency,
        ...(window === 'custom' ? { from: von, to: bis } : {}),
        ...(isins ? { isins: isins.join(',') } : {}),
      },
      responseType: 'blob',
    }),
  exportPrices: (isins: string[], currency = 'EUR') =>
    api.get('/api/watchlist/prices.csv', {
      params: { isins: isins.join(','), currency },
      responseType: 'blob',
    }),

  // ── Kurse aus einer Datei, als Paket (seit 07.09.2026) ─────────────────
  // Schritt 1: nur lesen. Erkannte Einstellungen, erste Zeilen, Spanne — und
  // die Stufen, die dieser Nutzer waehlen darf.
  previewPriceFile: (file: File, settings?: Partial<KursDateiEinstellungen>) => {
    const formData = new FormData();
    formData.append('file', file);
    einstellungenAnhaengen(formData, settings);
    return api.post('/api/watchlist/prices/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Schritt 2: schreiben. Entweder `itemId` (ergaenzen) oder Stammdaten mit
  // `isin` (anlegen). Ohne `beiDubletten` antwortet das Backend mit 409, sobald
  // Tage doppelt waeren — erst die Rueckfrage, dann derselbe Aufruf mit der
  // Antwort.
  uploadPriceFile: (file: File, ziel: KursDateiZiel) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', ziel.scope);
    if (ziel.beiDubletten) formData.append('bei_dubletten', ziel.beiDubletten);
    if (ziel.itemId !== undefined) formData.append('item_id', String(ziel.itemId));
    for (const [k, v] of Object.entries({
      isin: ziel.isin, name: ziel.name, currency: ziel.currency,
      ertragsverwendung: ziel.ertragsverwendung, instrument_type: ziel.instrumentType, wkn: ziel.wkn,
    })) {
      if (v) formData.append(k, v);
    }
    einstellungenAnhaengen(formData, ziel.settings);
    return api.post('/api/watchlist/prices', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Woher die Kurse eines Wertpapiers stammen: Anbieter-Spanne und jedes
  // Upload-Paket mit Datei, Zeitraum, Beitrag und Loeschrecht.
  sources: (itemId: Id) => api.get(`/api/watchlist/items/${itemId}/sources`),
  deleteUpload: (uploadId: Id) => api.delete(`/api/watchlist/uploads/${uploadId}`),
  // Stammdaten von Hand — je Feld Systemwert, Ueberschreibungen je Stufe,
  // geltender Wert. `null` in `values` nimmt die Ueberschreibung zurueck.
  stammdaten: (itemId: Id) => api.get(`/api/watchlist/items/${itemId}/stammdaten`),
  setStammdaten: (itemId: Id, scope: string, values: Record<string, string | null>) =>
    api.put(`/api/watchlist/items/${itemId}/stammdaten`, { scope, values }),
};

// === CSV Import ===
export const csvAPI = {
  preview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/simulation/csv/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPriceSources: (securityId: Id) => api.get(`/api/simulation/securities/${securityId}/price-sources`),
  deleteManualPrices: (securityId: Id, scope = 'user') => api.delete(`/api/simulation/securities/${securityId}/manual-prices`, { params: { scope } }),
  appendPrices: (securityId: Id, file: File, scope = 'user', beiDubletten?: 'ergaenzen' | 'ersetzen') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', scope);
    // Ohne Entscheidung antwortet das Backend mit 409, sobald Tage doppelt
    // waeren — erst die Rueckfrage, dann derselbe Aufruf mit der Antwort.
    if (beiDubletten) formData.append('bei_dubletten', beiDubletten);
    return api.post(`/api/simulation/securities/${securityId}/append-prices`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadTemplate: (templateType: string, format: string) => {
    return api.get(`/api/simulation/templates/${templateType}?format=${format}`, { responseType: 'blob' });
  },
};

// === Securities ===
export const securitiesAPI = {
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/securities/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/securities/${id}`),
};

// === Simulation ===
export const simulationAPI = {
  run: (settings: RequestBody) => api.post('/api/simulation/run', settings),
  // Wessen Lauf, sagt die Sitzung (Cookie) — keine Kennung im Pfad.
  getProgress: () => api.get('/api/simulation/progress'),
  cancel: () => api.post('/api/simulation/cancel'),
  listResults: () => api.get('/api/simulation/results'),
  getResult: (id: Id, displayCurrency = 'EUR') => api.get(`/api/simulation/results/${id}?display_currency=${displayCurrency}`),
  /** Bestenlisten unter Filtern — gerechnet von der Datenbank (`ergebnis_portfolios`). */
  getBestenlisten: (id: Id, filters: unknown[], displayCurrency = 'EUR', signal?: AbortSignal) => {
    const params = new URLSearchParams({ display_currency: displayCurrency, filters: JSON.stringify(filters) });
    return api.get(`/api/simulation/results/${id}/bestenlisten?${params}`, { signal });
  },
  deleteResult: (id: Id) => api.delete(`/api/simulation/results/${id}`),
  renameResult: (id: Id, name: string) => api.patch(`/api/simulation/results/${id}/name`, { name }),
  /** Vermögensverlauf einer Ergebniszeile mit Plan — gerechnet auf Abruf. */
  getResultVermoegen: (id: Id, data: RequestBody) => api.post(`/api/simulation/results/${id}/vermoegen`, data),
  // `beimVerlassen`: Die Seite schliesst gerade (Neuladen, Tab zu). Nur `fetch`
  // mit `keepalive` ueberlebt das — XHR bricht der Browser ab.
  updateFilters: (id: Id, filters: RequestBody[], { beimVerlassen = false } = {}) =>
    api.patch(`/api/simulation/results/${id}/filters`, filters,
      beimVerlassen ? { adapter: 'fetch', fetchOptions: { keepalive: true } } : undefined),
  updateColumnSettings: (id: Id, settings: RequestBody) => api.patch(`/api/simulation/results/${id}/columns`, settings),
  createFilteredTable: (id: Id, data: RequestBody) => api.post(`/api/simulation/results/${id}/create-filtered-table`, data),
  exportResult: (id: Id, listType: string, filters = [], format = 'csv', displayCurrency = 'EUR') => {
    // `display_currency`: Geldgrenzen der Filter gelten in der Anzeigewährung — wie auf der Seite.
    const params = new URLSearchParams({ list_type: listType, format: format, display_currency: displayCurrency });
    if (filters && filters.length > 0) {
      params.append('filters', JSON.stringify(filters));
    }
    return api.get(`/api/simulation/results/${id}/export?${params}`, { responseType: 'blob' });
  },
  // 2026-05 Phase 7: PDF-Report (separater Endpoint, ab Pro-Plan)
  exportResultPdf: (id: Id) =>
    api.get(`/api/simulation/results/${id}/pdf`, { responseType: 'blob' }),
  getExchangeRates: () => api.get('/api/simulation/exchange-rates/current'),
  getUserColumnDefaults: () => api.get('/api/simulation/user/column-defaults'),
  updateUserColumnDefaults: (settings: RequestBody) => api.patch('/api/simulation/user/column-defaults', settings),
};

// === Portfolio Lists (Favoriten) ===
export const portfolioListsAPI = {
  list: () => api.get('/api/simulation/portfolio-lists'),
  create: (data: RequestBody) => api.post('/api/simulation/portfolio-lists', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/portfolio-lists/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/portfolio-lists/${id}`),
  getFavorites: (listId: Id) => api.get(`/api/simulation/portfolio-lists/${listId}/favorites`),
  getAllWithFavorites: () => api.get('/api/simulation/favorites/all'),
  createTableFromList: (listId: Id, data = {}) => api.post(`/api/simulation/portfolio-lists/${listId}/create-table`, data),
  
  // Export-Funktion (CSV, XLSX, ODS)
  export: (listId: Id, format = 'csv') => api.get(
    `/api/simulation/portfolio-lists/${listId}/export?format=${format}`,
    { responseType: 'blob' }
  ),
  
  // Auto-Update Funktionen (auf Listen-Ebene)
  setAktualisierung: (listId: Id, aktualisierung: 'manuell' | 'bei_aufruf' | 'taeglich' | 'woechentlich' | 'monatlich') =>
    api.put(`/api/simulation/portfolio-lists/${listId}/aktualisierung`, { aktualisierung }),
  // Nach jeder automatischen Aktualisierung der Liste eine E-Mail (an/aus).
  setMail: (listId: Id, mail: boolean) => api.put(`/api/simulation/portfolio-lists/${listId}/mail`, { mail }),
  aktualisierungStatus: () => api.get('/api/simulation/portfolio-lists/aktualisierung/status'),
  refresh: (listId: Id) => api.post(`/api/simulation/portfolio-lists/${listId}/refresh`),
  
  // Arena-Toggle
  toggleArena: (enabled: boolean) => api.post('/api/simulation/arena/toggle', { enabled }),
};

// === Favorites ===
export const favoritesAPI = {
  // Kennzahlen je gespeichertem Portfolio und Zeitraum — der einblendbare
  // Abschnitt „Im Zeitverlauf" (aus der gespeicherten Reihe, nichts neu gerechnet).
  zeitverlauf: (zeitraeume: string[]) =>
    api.get<components['schemas']['FavoritenZeitverlauf']>('/api/simulation/favorites/zeitverlauf', {
      params: { zeitraeume: zeitraeume.join(',') },
    }),
  add: (data: RequestBody) => api.post('/api/simulation/favorites', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/favorites/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/favorites/${id}`),
  getByResult: (resultId: Id) => api.get(`/api/simulation/favorites/by-result/${resultId}`),
  
  // Auto-Update Funktionen
  refresh: (id: Id) => api.post(`/api/simulation/favorites/${id}/refresh`),
  /** Ab wann die Papiere Kurse haben — und ob das Portfolio früher beginnen kann. */
  kursbeginn: (id: Id) => api.get(`/api/simulation/favorites/${id}/kursbeginn`),
  /** Den Backtest auf den frühesten gemeinsamen Kurs vorziehen und neu rechnen. */
  frueherBeginnen: (id: Id) => api.post(`/api/simulation/favorites/${id}/frueher-beginnen`),
  
  // Time Series Chart Endpoints
  getChartData: (id: Id, period = 'max') => 
    api.get(`/api/simulation/favorites/${id}/chart-data`, { params: { period } }),
  getSecurityChartData: (id: Id, securityName: string, period = 'max') =>
    api.get(`/api/simulation/favorites/${id}/security-chart-data`, { params: { security: securityName, period } }),
  /** Vermögensverlauf mit Plan (Sparplan/Entnahme) aus der gespeicherten Zeitreihe. */
  getVermoegen: (id: Id) => api.get(`/api/simulation/favorites/${id}/vermoegen`),
};

// === Presets ===
// Gespeicherte Ist-Portfolios — dieselbe Art wie die Bank-Konditionen
// (`presetsAPI`), nur fuer die Aufteilung des eigenen Depots.
export const istPortfoliosAPI = {
  list: () => api.get('/api/simulation/ist-portfolios'),
  create: (data: RequestBody) => api.post('/api/simulation/ist-portfolios', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/ist-portfolios/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/ist-portfolios/${id}`),
};

/** Benannte Entnahmepläne — Beträge in EUR, wie am Ergebnis. */
export const entnahmeVorlagenAPI = {
  list: () => api.get('/api/simulation/entnahme-vorlagen'),
  create: (data: RequestBody) => api.post('/api/simulation/entnahme-vorlagen', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/entnahme-vorlagen/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/entnahme-vorlagen/${id}`),
};

/** Gespeicherte Monte-Carlo-Einstellungen: die Annahmen ohne Aufteilung
 * (`routers/monte_carlo_einstellungen.py`), Beträge in EUR. */
export const monteCarloEinstellungenAPI = {
  list: () => api.get('/api/simulation/monte-carlo-einstellungen'),
  create: (data: RequestBody) => api.post('/api/simulation/monte-carlo-einstellungen', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/monte-carlo-einstellungen/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/monte-carlo-einstellungen/${id}`),
};

/** Monte Carlo auf gespeicherten Portfolios (`routers/monte_carlo.py`). Gerechnet
 * wird im Worker: `start` antwortet sofort mit dem Lauf, `get` holt ihn ab. */
export const monteCarloAPI = {
  start: (favoriteId: Id, data: RequestBody) => api.post(`/api/simulation/favorites/${favoriteId}/monte-carlo`, data),
  get: (laufId: Id) => api.get(`/api/simulation/monte-carlo/${laufId}`),
  /** Seite „Ergebnisse“: fertige Läufe, neueste zuerst — ohne Fächer und Jahresverläufe. */
  ergebnisse: (params: { seite: number; pro_seite?: number }) => api.get('/api/simulation/monte-carlo/ergebnisse', { params }),
  /** Umbenennen; `name: null` gibt dem Lauf den Namen seiner Grundlage zurück. */
  umbenennen: (laufId: Id, data: { name: string | null }) => api.patch(`/api/simulation/monte-carlo/${laufId}`, data),
  loeschen: (laufId: Id) => api.delete(`/api/simulation/monte-carlo/${laufId}`),
  /** Ein Verlauf Jahr für Jahr als Datei (CSV für alle, Excel/ODS je nach Tarif), Beträge in EUR. */
  jahre: (laufId: Id, params: { verlauf: string; kaufkraft: 'heute' | 'nominal'; format: string }) =>
    api.get(`/api/simulation/monte-carlo/${laufId}/jahre`, { params, responseType: 'blob' }),
  /** Der Bericht eines fertigen Laufs als PDF (`pdf_export`); `titel` = Name der Grundlage, wie die Seite ihn zeigt. */
  bericht: (laufId: Id, titel: string | null) =>
    api.get(`/api/simulation/monte-carlo/${laufId}/pdf`, { params: titel ? { titel: titel.slice(0, 200) } : {}, responseType: 'blob' }),
  /** Ein Ergebnis per Link teilen — Abschrift, ohne Name und E-Mail (`routers/monte_carlo_freigabe.py`). */
  freigabe: {
    get: (laufId: Id) => api.get(`/api/simulation/monte-carlo/${laufId}/freigabe`),
    create: (laufId: Id, data: RequestBody) => api.post(`/api/simulation/monte-carlo/${laufId}/freigabe`, data),
    delete: (laufId: Id) => api.delete(`/api/simulation/monte-carlo/${laufId}/freigabe`),
  },
  /** Ein geteiltes Ergebnis — ohne Anmeldung lesbar. */
  geteilt: (token: string) => api.get(`/api/public/monte-carlo/geteilt/${encodeURIComponent(token)}`),
  list: (favoriteId: Id) => api.get(`/api/simulation/favorites/${favoriteId}/monte-carlo`),
  /** Mischungen aus Indexreihen (Seite `/monte-carlo`). */
  bausteine: () => api.get('/api/simulation/monte-carlo/bausteine'),
  /** Die gemerkten Wertpapiere in ihren Ordnern, je mit Datenfenster. */
  wertpapiere: () => api.get('/api/simulation/monte-carlo/wertpapiere'),
  /** Vor dem Start: wie lang die echte Kursgeschichte der Grundlage ist und ob
   * sich Krisenjahre ergänzen liessen (`mc_rueckrechnung.vorschau`). */
  geschichte: (data: RequestBody) => api.post('/api/simulation/monte-carlo/geschichte', data),
  startMischung: (data: RequestBody) => api.post('/api/simulation/monte-carlo/mischung', data),
  mischungsLaeufe: () => api.get('/api/simulation/monte-carlo/mischung/laeufe'),
  /** Seite `/monte-carlo`: die gespeicherten Portfolios zur Auswahl und die
   * letzten Läufe über beide Quellen. */
  portfolios: () => api.get('/api/simulation/monte-carlo/portfolios'),
  laeufe: () => api.get('/api/simulation/monte-carlo/laeufe'),
  /** Portfolios aus den Community-Backtests — frei für jeden Angemeldeten. */
  community: {
    /** Seitenweise, gesucht und sortiert auf dem Server (`q`, `sort`, `min_jahre`, `limit`, `auswahl`). */
    suche: (params: Record<string, string | number>) => api.get('/api/simulation/monte-carlo/community/suche', { params }),
    start: (eintragId: Id, data: RequestBody) => api.post(`/api/simulation/monte-carlo/community/${eintragId}`, data),
    laeufe: (eintragId: Id) => api.get(`/api/simulation/monte-carlo/community/${eintragId}/laeufe`),
  },
  mischungen: {
    list: () => api.get('/api/simulation/monte-carlo/mischungen'),
    create: (data: RequestBody) => api.post('/api/simulation/monte-carlo/mischungen', data),
    update: (id: Id, data: RequestBody) => api.put(`/api/simulation/monte-carlo/mischungen/${id}`, data),
    delete: (id: Id) => api.delete(`/api/simulation/monte-carlo/mischungen/${id}`),
    /** Ein Backtest-Portfolio oder einen Community-Eintrag als Aufteilung merken (Verweis, keine Kopie). */
    verweis: (data: RequestBody) => api.post('/api/simulation/monte-carlo/mischungen/verweis', data),
    /** Ordner der Seite „Aufteilungen“. */
    ordner: {
      list: () => api.get('/api/simulation/monte-carlo-aufteilung-ordner'),
      create: (data: RequestBody) => api.post('/api/simulation/monte-carlo-aufteilung-ordner', data),
      update: (id: Id, data: RequestBody) => api.put(`/api/simulation/monte-carlo-aufteilung-ordner/${id}`, data),
      delete: (id: Id) => api.delete(`/api/simulation/monte-carlo-aufteilung-ordner/${id}`),
    },
  },
};

/** Gespeicherte Monte-Carlo-Simulationen in Ordnern (Seite `/my-monte-carlo`). */
export const monteCarloAblageAPI = {
  ordner: {
    list: () => api.get('/api/simulation/monte-carlo-ordner'),
    create: (data: RequestBody) => api.post('/api/simulation/monte-carlo-ordner', data),
    update: (id: Id, data: RequestBody) => api.put(`/api/simulation/monte-carlo-ordner/${id}`, data),
    delete: (id: Id) => api.delete(`/api/simulation/monte-carlo-ordner/${id}`),
  },
  /** Einen fertigen Lauf in einen Ordner speichern (`ordner_id`, `lauf_id`, `name`). */
  speichern: (data: RequestBody) => api.post('/api/simulation/monte-carlo-favoriten', data),
  get: (id: Id) => api.get(`/api/simulation/monte-carlo-favoriten/${id}`),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/monte-carlo-favoriten/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/monte-carlo-favoriten/${id}`),
};

export const presetsAPI = {
  list: () => api.get('/api/simulation/presets'),
  get: (id: Id) => api.get(`/api/simulation/presets/${id}`),
  create: (data: RequestBody) => api.post('/api/simulation/presets', data),
  update: (id: Id, data: RequestBody) => api.put(`/api/simulation/presets/${id}`, data),
  delete: (id: Id) => api.delete(`/api/simulation/presets/${id}`),
};

// === Portfolio Widgets ===
export const widgetsAPI = {
  list: () => api.get('/api/widgets'),
  create: (data: RequestBody) => api.post('/api/widgets', data),
  get: (id: Id) => api.get(`/api/widgets/${id}`),
  update: (id: Id, data: RequestBody) => api.put(`/api/widgets/${id}`, data),
  delete: (id: Id) => api.delete(`/api/widgets/${id}`),
  getStats: (id: Id, days = 30) => api.get(`/api/widgets/${id}/stats`, { params: { days } }),
  
  // Für Favoriten
  getForFavorite: (favoriteId: Id) => api.get(`/api/widgets/favorite/${favoriteId}/widget`),
  getRanking: (favoriteId: Id) => api.get(`/api/widgets/favorite/${favoriteId}/ranking`),
};

// === Public Leaderboard (keine Authentifizierung) ===
const publicApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const publicLeaderboardAPI = {
  getMetrics: () => publicApi.get('/api/public/leaderboard/metrics'),
  getStats: () => publicApi.get(RANGLISTE_STATISTIK_PFAD),
  getLeaderboard: (metric: string, limit = RANGLISTE_LIMIT, country: string | null = null, lang: string | null = null, username: string | null = null) =>
    publicApi.get(ranglistenPfad(metric, limit, country, lang, username)),
  getArchitects: (country: string | null = null, lang: string | null = null) =>
    publicApi.get(architektenPfad(country, lang)),
  getCommunityFeed: (limit = 20, offset = 0) => publicApi.get(`/api/public/community/feed?limit=${limit}&offset=${offset}`),
  getTrending: () => publicApi.get('/api/public/community/trending'),
  /** Monte-Carlo-Vorschau eines Eintrags für Gäste — feste Annahmen, `laeuft` beim ersten Abruf. */
  monteCarloVorschau: (eintragId: Id) => publicApi.get(`/api/public/community/${eintragId}/monte-carlo`),
};

// === Altersvorsorgedepot-Rechner (keine Authentifizierung, nur Tabellen — gerechnet wird im Browser) ===
export const avDepotAPI = {
  aufteilungen: () => publicApi.get<components['schemas']['AvDepotAufteilungenResponse']>('/api/public/av-depot/aufteilungen'),
  faktoren: (aufteilung: string, kostenBp: number) =>
    publicApi.get<components['schemas']['AvDepotFaktorenResponse']>('/api/public/av-depot/faktoren', { params: { aufteilung, kosten_bp: kostenBp } }),
};

// === Musterportfolios (keine Authentifizierung, monatlich gerechnet — die Seiten zeigen nur) ===
export const musterportfolioAPI = {
  alle: () => publicApi.get<components['schemas']['MusterportfoliosResponse']>('/api/public/musterportfolios'),
  eines: (kennung: string) =>
    publicApi.get<components['schemas']['MusterportfolioResponse']>(`/api/public/musterportfolios/${encodeURIComponent(kennung)}`),
};

// === Twelve Data API (Online-Import) ===
export const twelvedataAPI = {
  // Suche
  search: (query: string, types: string[] | null = null, limit = 30) => {
    const params: QueryParams = { query, limit };
    if (types) params.types = types;
    return api.get('/api/twelvedata/search', { params });
  },
  // Import
  importSecurity: (tableId: Id, data: RequestBody) => api.post(`/api/twelvedata/import/${tableId}`, data),
  // Ohne Datensatz: legt nur den Fonds mit seinen Kursen an und merkt ihn im
  // Bestand. Erster Schritt des Ablaufs — Wertpapiere anlegen, dann Datensaetze
  // daraus bauen. Dieselbe Funktion im Backend, nur ohne `table_id`.
  importToStock: (data: RequestBody) => api.post('/api/twelvedata/import', data),
  // Zeitreihen-Länge ermitteln
  getEarliestTimestampsBatch: (symbols: string[]) => api.post('/api/twelvedata/earliest-timestamps', symbols),
  // Debug: Alle Börsen-Quellen für eine ISIN anzeigen
  getSourcesForIsin: (isin: string) => api.get(`/api/twelvedata/debug/sources/${isin}`),
};

// === Admin API (System-Einstellungen) ===
export const adminAPI = {
  // Exchange Rates
  getExchangeRatesDetails: () => api.get('/api/admin/exchange-rates/details'),
  triggerExchangeRatesUpdate: () => api.post('/api/admin/exchange-rates/update'),
  // Verwaltungsübersicht: eine Zeile je Themengruppe mit Ampel (Audit V11)
  getUebersicht: () => api.get('/api/admin/uebersicht'),
  // Gattungen eines Wertpapiers — jeder angemeldete Nutzer (Auswahlfelder der Dialoge)
  getInstrumentTypes: () => api.get('/api/admin/instrument-types'),
  // Twelve Data API-Key
  getTwelvedataStatus: () => api.get('/api/admin/twelvedata/status'),
  getPriceFreshness: () => api.get('/api/admin/twelvedata/price-freshness'),
  setTwelvedataApiKey: (apiKey: string) => api.post('/api/admin/twelvedata/api-key', { api_key: apiKey }),
  testTwelvedataApiKey: (apiKey: string) => api.post('/api/admin/twelvedata/test', { api_key: apiKey }),
  deleteTwelvedataApiKey: () => api.delete('/api/admin/twelvedata/api-key'),
  // Wartung der Kursreihen — starten einen Hintergrundlauf; seinen Zustand
  // fragt `useLaufZustand` (components/admin/anreicherung/Berichte.jsx) ab
  backfillPriceHistory: (data: RequestBody) => api.post('/api/admin/twelvedata/backfill-history', data),
  repairFundSymbols: (data: RequestBody) => api.post('/api/admin/twelvedata/repair-symbols', data),
  // DeepL API-Key
  getDeeplStatus: () => api.get('/api/admin/deepl/status'),
  setDeeplApiKey: (apiKey: string) => api.post('/api/admin/deepl/api-key', { api_key: apiKey }),
  testDeeplApiKey: (apiKey: string) => api.post('/api/admin/deepl/test', { api_key: apiKey }),
  deleteDeeplApiKey: () => api.delete('/api/admin/deepl/api-key'),
  // Brevo-Schlüssel der Plattform — Fundament-Vertrag `brevo_schluessel`:
  // GET/POST/DELETE antworten alle mit dem Stand (`BrevoStand`), nie mit dem Wert.
  getBrevoSchluessel: () => api.get<BrevoStand>('/api/admin/brevo-schluessel'),
  // Ohne `fremdesKontoErlauben` sagt das Backend bei einem Schlüssel aus einem
  // anderen als dem Flotten-Konto 409 ab — die Karte fragt dann „Trotzdem speichern“.
  setBrevoSchluessel: (apiKey: string, fremdesKontoErlauben: boolean) =>
    api.post<BrevoStand>('/api/admin/brevo-schluessel', { api_key: apiKey, fremdes_konto_erlauben: fremdesKontoErlauben }),
  deleteBrevoSchluessel: () => api.delete<BrevoStand>('/api/admin/brevo-schluessel'),
  // Instrument-Typen
  // Börsennamen
  getExchangeNames: () => api.get('/api/admin/exchange-names'),
  createExchangeName: (data: RequestBody) => api.post('/api/admin/exchange-names', data),
  updateExchangeName: (micCode: string, data: RequestBody) => api.put(`/api/admin/exchange-names/${micCode}`, data),
  deleteExchangeName: (micCode: string) => api.delete(`/api/admin/exchange-names/${micCode}`),
  resetExchangeNames: () => api.post('/api/admin/exchange-names/reset'),
  // Nutzerverwaltung
  getUsers: (params = {}) => api.get('/api/admin/users', { params }),
  getUsersStats: () => api.get('/api/admin/users/stats'),
  getUser: (userId: Id) => api.get(`/api/admin/users/${userId}`),
  getUserTables: (userId: Id) => api.get(`/api/admin/users/${userId}/tables`),
  updateUser: (userId: Id, data: RequestBody) => api.patch(`/api/admin/users/${userId}`, data),
  resetUserPassword: (userId: Id) => api.post(`/api/admin/users/${userId}/reset-password`),
  deleteUser: (userId: Id) => api.delete(`/api/admin/users/${userId}`),
  // Seite eines Nutzers (seit 17.09.2026): Verwaltungsaktionen blätterbar,
  // Support-Zugriff, interne Kommentare mit Tags.
  getUserActivity: (userId: Id, params: { page: number; per_page: number; scope: 'actor' | 'subject' | 'all' }) =>
    api.get(`/api/admin/users/${userId}/activity`, { params }),
  getUserSupportAccess: (userId: Id) => api.get(`/api/admin/users/${userId}/support-access`),
  requestUserSupportAccess: (userId: Id, reason: string) =>
    api.post(`/api/admin/users/${userId}/support-access/request`, { reason: reason || null }),
  getUserNotes: (userId: Id) => api.get(`/api/admin/users/${userId}/notes`),
  // Start-Hinweise (routers/admin_start_hinweise.py): je Nutzer und für alle.
  getStartHinweise: () => api.get<components['schemas']['StartHinweisStand'][]>('/api/admin/start-hinweise'),
  startHinweisAllenAnzeigen: (hinweis: StartHinweisArt) =>
    api.post<components['schemas']['StartHinweisStand'][]>(`/api/admin/start-hinweise/${hinweis}/allen-anzeigen`),
  getNutzerStartHinweise: (userId: Id) =>
    api.get<components['schemas']['NutzerStartHinweis'][]>(`/api/admin/users/${userId}/start-hinweise`),
  nutzerStartHinweisWiederAnzeigen: (userId: Id, hinweis: StartHinweisArt) =>
    api.delete<components['schemas']['NutzerStartHinweis'][]>(`/api/admin/users/${userId}/start-hinweise/${hinweis}`),
  createUserNote: (userId: Id, text: string, tagIds: Id[]) =>
    api.post(`/api/admin/users/${userId}/notes`, { text, tag_ids: tagIds }),
  updateUserNote: (userId: Id, noteId: Id, data: { text?: string; tag_ids?: Id[] }) =>
    api.patch(`/api/admin/users/${userId}/notes/${noteId}`, data),
  deleteUserNote: (userId: Id, noteId: Id) => api.delete(`/api/admin/users/${userId}/notes/${noteId}`),
  getNoteTags: () => api.get('/api/admin/note-tags'),
  createNoteTag: (name: string) => api.post('/api/admin/note-tags', { name }),
  deleteNoteTag: (tagId: Id) => api.delete(`/api/admin/note-tags/${tagId}`),
  // Organisationen/Teams
  getOrganizations: () => api.get('/api/admin/organizations'),
  // Corporates
  getCorporateStats: () => api.get('/api/admin/corporates/stats'),
  getCorporates: (params = {}) => api.get('/api/admin/corporates', { params }),
  getCorporate: (id: Id) => api.get(`/api/admin/corporates/${id}`),
  createCorporate: (data: RequestBody) => api.post('/api/admin/corporates', data),
  deleteCorporate: (id: Id) => api.delete(`/api/admin/corporates/${id}`),
  // Umbenennen (Fundament account_name) — derselbe PATCH, nur der Name.
  renameCorporate: (id: Id, name: string) => api.patch(`/api/admin/corporates/${id}`, { name }),
  configureCorporateSSO: (id: Id, data: RequestBody) => api.put(`/api/admin/corporates/${id}/sso`, data),
  // E-Mail-Domain-Whitelist (Plattformadmin)
  getCorporateEmailDomains: (id: Id) => api.get(`/api/admin/corporates/${id}/email-domains`),
  addCorporateEmailDomain: (id: Id, domain: string) => api.post(`/api/admin/corporates/${id}/email-domains`, { domain }),
  deleteCorporateEmailDomain: (id: Id, domainId: Id) => api.delete(`/api/admin/corporates/${id}/email-domains/${domainId}`),
  // Einladungen
  getInvitations: (params = {}) => api.get('/api/admin/invitations', { params }),
  createInvitation: (data: RequestBody) => api.post('/api/admin/invitations', data),
  revokeInvitation: (id: Id) => api.delete(`/api/admin/invitations/${id}`),
  resendInvitationEmail: (id: Id) => api.post(`/api/admin/invitations/${id}/resend`),
  // Die Einladungsmail, wie sie rausginge (über denselben Weg wie der Versand).
  invitationMailVorschau: (data: EinladungsMailVorschauIn) =>
    api.post<EinladungsMailVorschau>('/api/admin/invitations/mail-vorschau', data),
  // Erweiterte Nutzerverwaltung
  //
  // `deleteUserSoft`/`deleteUserHard` standen hier bis zum 14.09.2026 und
  // schickten einen Rumpf an `DELETE /api/admin/users/{id}`. Diesen Rumpf hat
  // nie jemand gelesen: Der Backend-Handler, der auf dem Pfad tatsächlich
  // läuft, nimmt keinen — er löscht immer endgültig. `deleteUserSoft` hätte
  // also deaktiviert versprochen und gelöscht. Es gibt jetzt genau eine
  // Methode für den Pfad: `deleteUser` weiter oben. Deaktiviert wird über
  // `updateUser(id, { is_active: false })`.
  //
  // `reactivateUser` ist am selben Tag entfallen: Jeder lebende Schreiber von
  // `deleted_at` anonymisiert die Zeile unwiderruflich, eine Reaktivierung
  // hätte also nur ein Konto ohne Anmeldedaten wieder öffentlich sichtbar
  // gemacht. Aktiviert wird ebenfalls über `updateUser(id, { is_active: true })`.
  sendPasswordResetEmail: (userId: Id) => api.post(`/api/admin/users/${userId}/send-password-reset`),
  // Gutschrift der Verwaltung: ein Credit-Paket mit Grund, so lange gültig wie das Startguthaben
  grantCredits: (userId: number, credits: number, grund: string) =>
    api.post<components['schemas']['CreditGutschriftResponse']>('/api/sim-credits/admin/grant', { user_id: userId, credits, grund }),
};

// === Fund Enrichment API ===
export const enrichmentAPI = {
  /** Die letzten Läufe je Anbieter und der Wartung, die der Server noch kennt (Wiederaufnahme). */
  laeufe: () => api.get('/api/enrichment/laeufe'),
  /** Ein Wertpapier in den Systemordner der Merkliste — oder heraus. */
  setzeTestdaten: (isin: string, drin: boolean) =>
    api.put(`/api/enrichment/fund/${isin}/testdaten`, { drin }),
  // Claude API Key Management
  getClaudeStatus: () => api.get('/api/enrichment/claude/status'),
  setClaudeApiKey: (apiKey: string) => api.post('/api/enrichment/claude/api-key', { api_key: apiKey }),
  testClaudeApiKey: (apiKey: string) => api.post('/api/enrichment/claude/test', { api_key: apiKey }),
  deleteClaudeApiKey: () => api.delete('/api/enrichment/claude/api-key'),

  // Claude Model Management
  getClaudeModels: () => api.get('/api/enrichment/claude/models'),
  getCurrentModel: () => api.get('/api/enrichment/claude/model'),
  setClaudeModel: (modelId: Id) => api.post(`/api/enrichment/claude/model?model_id=${modelId}`),

  // Monatsbudget für Claude (EUR rein, abgelegt in USD) — jede Antwort ist der neue Stand
  getClaudeBudget: () => api.get<BudgetStand>('/api/enrichment/budget'),
  setClaudeBudget: (budgetEur: number) => api.post<BudgetStand>('/api/enrichment/budget', { budget_eur: budgetEur }),
  deleteClaudeBudget: () => api.delete<BudgetStand>('/api/enrichment/budget'),

  // Enrichment Settings
  getEnrichmentMaxAge: () => api.get('/api/enrichment/settings/enrichment-max-age'),
  setEnrichmentMaxAge: (days: number) => api.post(`/api/enrichment/settings/enrichment-max-age?days=${days}`),

  // Fund Providers
  getProviders: (sortBy = 'name', order = 'asc') =>
    api.get(`/api/enrichment/providers?sort_by=${sortBy}&order=${order}`),
  initializeProviders: () => api.post('/api/enrichment/providers/initialize'),

  // Catalog Management
  getCatalog: (params = {}) => api.get('/api/enrichment/catalog', { params }),
  getCatalogStats: () => api.get('/api/enrichment/catalog/stats'),
  getCatalogEntry: (isin: string) => api.get(`/api/enrichment/catalog/${isin}`),
  deleteCatalogEntry: (isin: string) => api.delete(`/api/enrichment/catalog/${isin}`),
  deleteCatalogByProvider: (providerId: Id) => api.delete(`/api/enrichment/catalog/provider/${providerId}`),
  deleteAllCatalog: () => api.delete(`/api/enrichment/catalog/all?confirm=DELETE_ALL`),

  // Fund Enrichment
  getEnrichment: (isin: string) => api.get(`/api/enrichment/fund/${isin}`),
  // Stammdaten von Hand — derselbe Weg wie in der Merkliste, nur auf der ISIN.
  // `updateEnrichment` stand hier bis zum 14.09.2026: Es schickte API-Namen an
  // eine Route, die sie an Spalten setzen wollte, die anders heissen — elf von
  // dreizehn Feldern verschwanden still, und der Abschluss der Route warf einen
  // AttributeError.
  stammdaten: (isin: string) => api.get(`/api/enrichment/fund/${isin}/stammdaten`),
  setStammdaten: (isin: string, scope: string, values: Record<string, string | null>) =>
    api.put(`/api/enrichment/fund/${isin}/stammdaten`, { scope, values }),
  // `funds.provider_id` ist die Spalte, über die die Datenquellen-Seite
  // gruppiert — setzen konnte sie bis zum 14.09.2026 niemand.
  setProvider: (isin: string, providerId: number | null) =>
    api.put(`/api/enrichment/fund/${isin}/provider`, { provider_id: providerId }),
  // Sektor- und Länderverteilung von Hand — der einzige Weg dorthin war eine
  // CSV-Datei, und für einen einzelnen falschen Prozentsatz ist das zu viel.
  setVerteilung: (isin: string, art: 'sector' | 'country', werte: Record<string, number>) =>
    api.put(`/api/enrichment/fund/${isin}/verteilung`, { art, werte }),
  // Die Börse, aus der die (plattformweite) Kursreihe eines Fonds kommt.
  // Antwortet mit `ok` + `grund`; eine Absage hat nichts geändert.
  notierungWechseln: (isin: string, notierung: { symbol: string; exchange: string }) =>
    api.post(`/api/enrichment/fund/${isin}/notierung`, notierung),
  // Welcher Posten der Systematik ein Branchen- oder Ländername ist
  // (`verteilung_systematik`): was die eingebaute Zuordnung nicht kennt, steht
  // unter `offen`; eine Entscheidung gilt beim nächsten Lesen überall.
  getVerteilungZuordnung: () => api.get('/api/enrichment/verteilung/zuordnung'),
  setVerteilungZuordnung: (dimension: 'branche' | 'land', name: string, ziel: string) =>
    api.put('/api/enrichment/verteilung/zuordnung', { dimension, name, ziel }),
  deleteVerteilungZuordnung: (id: number) => api.delete(`/api/enrichment/verteilung/zuordnung/${id}`),
  // Kurse und Ausschüttungen aus einer Datei, für alle Nutzer (seit 15.09.2026).
  // Vorher lief beides über `/catalog/{isin}/import-historical`, das Kurse ohne
  // Paket als Anbieterdaten ablegte. Die Kurs-Vorschau teilt sich die Route mit
  // der Merkliste (`watchlistAPI.previewPriceFile`) — sie schreibt nichts.
  kursquellen: (isin: string) => api.get(`/api/enrichment/fund/${isin}/kursquellen`),
  uploadKurse: (isin: string, file: File, ziel: KatalogDateiZiel) => {
    const formData = new FormData();
    formData.append('file', file);
    if (ziel.beiDubletten) formData.append('bei_dubletten', ziel.beiDubletten);
    if (ziel.currency) formData.append('currency', ziel.currency);
    einstellungenAnhaengen(formData, ziel.settings);
    return api.post(`/api/enrichment/fund/${isin}/kurse`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  ausschuettungenVorschau: (isin: string, file: File, betragsSpalte?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (betragsSpalte !== undefined) formData.append('amount_column', String(betragsSpalte));
    return api.post(`/api/enrichment/fund/${isin}/ausschuettungen/vorschau`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // `trotzAbweichung`: Das Backend misst, ob der Kurs an den Tagen der Datei um
  // die Beträge fällt (Ex-Tag), und lehnt sonst mit 409 `dividend_break` ab.
  // Erst nach der Rückfrage im Dialog geht die Datei mit diesem Schalter durch.
  uploadAusschuettungen: (
    isin: string,
    file: File,
    ziel: KatalogDateiZiel & { betragsSpalte?: number; trotzAbweichung?: boolean },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (ziel.beiDubletten) formData.append('bei_dubletten', ziel.beiDubletten);
    if (ziel.trotzAbweichung) formData.append('trotz_abweichung', 'true');
    if (ziel.currency) formData.append('currency', ziel.currency);
    if (ziel.betragsSpalte !== undefined) formData.append('amount_column', String(ziel.betragsSpalte));
    return api.post(`/api/enrichment/fund/${isin}/ausschuettungen`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteEnrichment: (isin: string) => api.delete(`/api/enrichment/fund/${isin}`),
  getStats: () => api.get('/api/enrichment/stats'),
  detectProvider: (isin: string, fundName: string) =>
    api.post(`/api/enrichment/detect-provider?isin=${isin}&fund_name=${encodeURIComponent(fundName || '')}`),

  // `smartEnrich`/`smartEnrichAndSave` standen hier bis zum 15.09.2026. Der
  // Speicher-Weg schrieb KI-Werte ohne Herkunft und ohne Vorrangregel in die
  // Spalten; die KI läuft jetzt nur noch über `POST /distributions/{isin}`.

  // Danger Zone - Admin Lösch-Funktionen
  getDangerZoneStats: () => api.get('/api/enrichment/danger-zone/stats'),
  deleteAllFundData: (includeUserSecurities = false) =>
    api.delete(`/api/enrichment/danger-zone/all-fund-data?confirm=DELETE_ALL_FUND_DATA&include_user_securities=${includeUserSecurities}`),
  deleteUserSecuritiesOnly: () =>
    api.delete('/api/enrichment/danger-zone/user-securities-only?confirm=DELETE_USER_SECURITIES'),
  deleteAllSimulationTables: () =>
    api.delete('/api/enrichment/danger-zone/simulation-tables?confirm=DELETE_ALL_SIMULATIONS'),
};


// === Internationalisierung (i18n) ===
export const i18nAPI = {
  // Öffentliche Endpoints
  getLanguages: (includeInactive = false) => api.get(`/api/i18n/languages?include_inactive=${includeInactive}`),
  getLanguage: (code: string) => api.get(`/api/i18n/languages/${code}`),
  getCountries: (includeInactive = false) => api.get(`/api/i18n/countries?include_inactive=${includeInactive}`),
  getCountriesWithRanking: () => api.get('/api/i18n/countries/with-ranking'),
  getLocaleConfig: () => api.get('/api/i18n/locale-config'),
  getTranslations: (language: string, category = null) => {
    let url = `/api/i18n/translations/${language}`;
    if (category) url += `?category=${category}`;
    return api.get(url);
  },
  getLanguageByPrefix: (prefix: string) => api.get(`/api/i18n/url-prefix/${prefix}`),
  getActivePrefixes: () => api.get('/api/i18n/active-prefixes'),
  
  // User-Präferenzen
  updateUserPreferences: (data: RequestBody) => api.put('/api/i18n/user/preferences', null, { params: data }),
};


// === Admin i18n ===
export const adminI18nAPI = {
  // Sprachen
  getLanguages: () => api.get('/api/admin/i18n/languages'),
  createLanguage: (data: RequestBody) => api.post('/api/admin/i18n/languages', data),
  updateLanguage: (code: string, data: RequestBody) => api.put(`/api/admin/i18n/languages/${code}`, data),
  launchLanguage: (code: string, engine = 'claude', mode = 'missing') => 
    api.post(`/api/admin/i18n/languages/${code}/launch?engine=${engine}&mode=${mode}`),
  getTranslationStatus: (code: string) => api.get(`/api/admin/i18n/languages/${code}/translation-status`),
  
  // Translation Keys (Master-Liste)
  
  // Übersetzungen
  getTranslations: (language: string, params = {}) => api.get(`/api/admin/i18n/translations/${language}`, { params }),
  updateTranslation: (language: string, key: string, data: RequestBody) => api.put(`/api/admin/i18n/translations/${language}/${encodeURIComponent(key)}`, data),
  markTranslationReviewed: (language: string, key: string) => api.post(`/api/admin/i18n/translations/${language}/${encodeURIComponent(key)}/review`),
  retranslate: (language: string, data: RequestBody) => api.post(`/api/admin/i18n/languages/${language}/retranslate`, data),
  
  // Länder
  getCountries: () => api.get('/api/admin/i18n/countries'),
  createCountry: (data: RequestBody) => api.post('/api/admin/i18n/countries', data),
  updateCountry: (code: string, data: RequestBody) => api.put(`/api/admin/i18n/countries/${code}`, data),
  updateCountryStats: () => api.post('/api/admin/i18n/countries/update-stats'),
  initCountries: () => api.post('/api/admin/i18n/countries/init'),
  
  // Utility
  
  // User Management (Admin)
  deleteUser: (userId: Id) => api.delete(`/api/admin/roles/users/${userId}`),
};

// === Team/Organisation API ===
export const teamAPI = {
  // Organisation
  getMyOrganization: () => api.get('/api/team/my-organization'),
  createOrganization: (data: RequestBody) => api.post('/api/team/organizations', data),
  // Account umbenennen (nur der Inhaber, Fundament account_name).
  renameOrganization: (orgId: Id, name: string) => api.put(`/api/team/organizations/${orgId}`, { name }),
  
  // Members
  listMembers: (orgId: Id) => api.get(`/api/team/organizations/${orgId}/members`),
  inviteMember: (orgId: Id, email: string) => api.post(`/api/team/organizations/${orgId}/invite`, { email }),
  inviteMembersBatch: (orgId: Id, emails: string[]) => api.post(`/api/team/organizations/${orgId}/invite-batch`, { emails }),
  removeMember: (orgId: Id, memberId: Id) => api.delete(`/api/team/organizations/${orgId}/members/${memberId}`),
  leaveOrganization: (orgId: Id) => api.post(`/api/team/organizations/${orgId}/leave`),
  
  // Invitations
  listInvitations: (orgId: Id) => api.get(`/api/team/organizations/${orgId}/invitations`),
  cancelInvitation: (orgId: Id, invitationId: Id) => api.delete(`/api/team/organizations/${orgId}/invitations/${invitationId}`),
  resendInvitation: (orgId: Id, invitationId: Id) => api.post(`/api/team/organizations/${orgId}/invitations/${invitationId}/resend`),
  
  // Public invitation endpoints
  getInvitationInfo: (token: string) => api.get(`/api/team/invitation/${token}`),
  acceptInvitation: (token: string) => api.post(`/api/team/invitation/${token}/accept`),

  // E-Mail-Domain-Whitelist (read-only für Owner/Member)
  listEmailDomains: (orgId: Id) => api.get(`/api/team/organizations/${orgId}/email-domains`),

  // Credits des Accounts — Verteilung durch den Inhaber (`services/credits`)
  getCredits: (orgId: Id) =>
    api.get<components['schemas']['AccountCreditsResponse']>(`/api/team/organizations/${orgId}/credits`),
  setCredits: (orgId: Id, data: components['schemas']['AccountCreditsSetzen']) =>
    api.put<components['schemas']['AccountCreditsResponse']>(`/api/team/organizations/${orgId}/credits`, data),
};

// System API (Fehler-Logging)
export const systemAPI = {
  // Log erstellen (für Frontend-Fehler)
  
  // Logs abrufen (Admin)
  getLogs: (params: QueryParams) => api.get('/api/admin/system/logs', { params }),
  getLogStats: () => api.get('/api/admin/system/logs/stats'),
  resolveLog: (id: Id, note: string) => api.put(`/api/admin/system/logs/${id}/resolve`, null, { params: { note } }),
  resolveAllLogs: (level: string, source: string) => api.put('/api/admin/system/logs/resolve-all', null, { params: { level, source } }),
  deleteLog: (id: Id) => api.delete(`/api/admin/system/logs/${id}`),
  cleanupLogs: (days: number, keepUnresolved: boolean) => api.delete('/api/admin/system/logs/cleanup', { params: { days, keep_unresolved: keepUnresolved } }),
  
  // Site Config
  getSiteConfig: () => api.get('/api/admin/system/site-config'),
  updateSiteConfig: (data: RequestBody) => api.put('/api/admin/system/site-config', data),
  
  // Branding Images
  uploadBrandingImage: (imageType: string, imageData: string, mimeType: string) => 
    api.post(`/api/admin/system/branding/${imageType}`, { image_data: imageData, mime_type: mimeType }),
  deleteBrandingImage: (imageType: string) => api.delete(`/api/admin/system/branding/${imageType}`),
  getBrandingImage: (imageType: string) => api.get(`/api/admin/system/branding/${imageType}`),
  
  // Page SEO
  getPageSeoList: () => api.get('/api/admin/page-seo'),
  getPageSeo: (pageKey: string, language: string) => api.get(`/api/admin/page-seo/${pageKey}/${language}`),
  updatePageSeo: (pageKey: string, language: string, data: RequestBody) => api.put(`/api/admin/page-seo/${pageKey}/${language}`, data),
  translatePageSeo: (targetLanguages: string[]) => api.post('/api/admin/page-seo/translate', targetLanguages),
  
  // robots.txt
  getRobotsTxt: () => api.get('/api/admin/robots-txt'),
  updateRobotsTxt: (content: string) => api.put('/api/admin/robots-txt', { content }),
};

export default api;
